import React, { useEffect, useState } from 'react';
import { Toaster } from '../components/ui/toaster';
import ReviewsMarquee from '../components/ReviewsMarquee';
import TrustBar from '../components/TrustBar';
import HammerNav from '../components/hammer/HammerNav';
import HammerHero from '../components/hammer/HammerHero';
import HammerFeatures from '../components/hammer/HammerFeatures';
import HammerSpecs from '../components/hammer/HammerSpecs';
import HammerInAction from '../components/hammer/HammerInAction';
import HammerGallery from '../components/hammer/HammerGallery';
import HammerDivider from '../components/hammer/HammerDivider';
import ServiceArea from '../components/ServiceArea';
import ShippingEstimator from '../components/ShippingEstimator';
import HammerAbout from '../components/hammer/HammerAbout';
import HammerFAQ, { HAMMER_FAQ_ITEMS } from '../components/hammer/HammerFAQ';
import HammerContact from '../components/hammer/HammerContact';
import HammerFooter from '../components/hammer/HammerFooter';
import { HAMMER_REVIEWS } from '../data/hammerReviews';

const PAGE_TITLE =
  'Absolute Hammer — Pneumatic Pile Driving Hammer for Sale | $10,500';
const META_DESCRIPTION =
  'The Absolute Hammer — a fully galvanized pneumatic pile-driving hammer built in Fort Myers, FL. Off-hammer proprietary controller, 1-year electronics warranty, ships nationwide. $10,500.';
const CANONICAL = 'https://absolutehammers.com/';

/**
 * Sets document-level SEO tags without needing react-helmet. Idempotent —
 * removes any tags it previously created when the component unmounts so
 * navigating back to the barge home page restores that page's own tags.
 */
function useSeo() {
  useEffect(() => {
    const managed = [];
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    const upsertMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        managed.push(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      return () => {
        if (prev != null) el.setAttribute('content', prev);
      };
    };

    const restoreDesc = upsertMeta('name', 'description', META_DESCRIPTION);
    const restoreOgTitle = upsertMeta('property', 'og:title', PAGE_TITLE);
    const restoreOgDesc = upsertMeta('property', 'og:description', META_DESCRIPTION);
    const restoreOgUrl = upsertMeta('property', 'og:url', CANONICAL);
    const restoreTwTitle = upsertMeta('name', 'twitter:title', PAGE_TITLE);
    const restoreTwDesc = upsertMeta('name', 'twitter:description', META_DESCRIPTION);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
      managed.push(canonical);
    }
    canonical.setAttribute('href', CANONICAL);

    // Product JSON-LD lives statically in public/index.html so crawlers see
    // it without executing JS — only FAQPage is injected here.

    // FAQPage JSON-LD — sourced from the same HAMMER_FAQ_ITEMS array the
    // visible FAQ section renders from, so the two never drift apart.
    const faqJsonld = document.createElement('script');
    faqJsonld.type = 'application/ld+json';
    faqJsonld.id = 'hammer-faq-jsonld';
    faqJsonld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: HAMMER_FAQ_ITEMS.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    });
    document.head.appendChild(faqJsonld);
    managed.push(faqJsonld);

    return () => {
      document.title = prevTitle;
      restoreDesc();
      restoreOgTitle();
      restoreOgDesc();
      restoreOgUrl();
      restoreTwTitle();
      restoreTwDesc();
      if (prevCanonical && canonical) canonical.setAttribute('href', prevCanonical);
      managed.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, []);
}

export default function HammerLanding() {
  useSeo();

  // Live Google reviews: fetched from our backend per page view (Google policy
  // forbids caching). Falls back silently to the static list until the Places
  // API credentials are set or if the fetch ever fails.
  const [reviews, setReviews] = useState(HAMMER_REVIEWS);
  const [reviewsLive, setReviewsLive] = useState(false);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('bad status'))))
      .then((d) => {
        if (d.source === 'google' && Array.isArray(d.reviews) && d.reviews.length) {
          setReviews(d.reviews);
          setReviewsLive(true);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="App bg-black min-h-screen" data-testid="hammer-landing-page">
      <HammerNav />
      <main>
        <HammerHero />
        <TrustBar />
        <HammerFeatures />
        <HammerSpecs />
        <HammerInAction />
        <HammerGallery />
        <ReviewsMarquee
          testId="reviews-hammer"
          title="What Absolute Hammer owners say"
          caption={reviewsLive ? 'Live Google reviews — shown in Google’s relevance order' : undefined}
          reviews={reviews}
          direction="ltr"
        />
        <HammerDivider />
        <ServiceArea />
        <ShippingEstimator />
        <HammerAbout />
        <HammerFAQ />
        <HammerContact />
      </main>
      <HammerFooter />
      <Toaster />
    </div>
  );
}
